/**
 * PipelineTable — one row per deal, sortable on every column.
 *
 * Answers the four complaints the card view could not:
 *
 *   can't compare side by side   a table is the comparison. Fifteen deals fit
 *                                on a screen where three cards did.
 *   doesn't show the new data    in-place rent, market rent, the gap, OpEx and
 *                                the evidence count come straight from the comp
 *                                database and sit in the row.
 *   stage is the wrong cut       stage is a column like any other. Sort by
 *                                mark-to-market, by rolling SF, by anything.
 *   too many clicks              the row is the summary. Opening a deal is for
 *                                when you have a question, not to find out
 *                                whether you have one.
 *
 * A column with no data shows an em dash, never a zero. A deal with no OpEx and
 * a deal with $0 of OpEx are different facts and the table has to keep them
 * apart, because sorting on the column will otherwise rank the unknown as
 * cheapest.
 */

import { useEffect, useMemo, useState } from "react";
import { loadPipelineMetrics } from "../../supabase";
import { fmt } from "../../utils/formatters";

const money = (v) => (Number.isFinite(v) && v !== 0 ? fmt.currencyOrDash(v) : "—");
const psf = (v) => (Number.isFinite(v) && v > 0 ? `$${Number(v).toFixed(2)}` : "—");
const sf0 = (v) => (Number.isFinite(Number(v)) && Number(v) ? Number(v).toLocaleString() : "—");
const pct = (v) => (Number.isFinite(v) ? `${(v * 100).toFixed(0)}%` : "—");

const STAGE_TONE = {
  Acquired: "bg-[rgba(79,157,118,0.26)] text-[#a9dcc0]",
  "In Diligence": "bg-[rgba(79,157,118,0.12)] text-[#7cbb99]",
  "LOI Submitted": "bg-[rgba(233,53,41,0.10)] text-[#f04f44]",
  Underwriting: "bg-[rgba(200,146,47,0.12)] text-[#d6a955]",
  Screening: "bg-[#3a3a3a] text-[#b4b4b4]",
  Tracking: "bg-[#2c2c2c] text-[#8a8a8a]",
  Passed: "bg-[#2c2c2c] text-[#8a8a8a]",
  Lost: "bg-[rgba(233,53,41,0.10)] text-[#e93529]",
  Dead: "bg-[#2c2c2c] text-[#7a7a7a]",
  Sourcing: "bg-[#2c2c2c] text-[#b4b4b4]",
  Outreach: "bg-[#2c2c2c] text-[#b4b4b4]",
};

/** Columns are declared, not hand-written, so sorting is uniform. */
const COLUMNS = [
  { k: "name", label: "Deal", w: "min-w-[15rem]", align: "left",
    get: (d) => d._name, render: (d) => (
      <div className="min-w-0">
        <div className="text-[#f4f4f4] truncate">{d._name}</div>
        <div className="text-[11px] text-[#8a8a8a] truncate">{d.addresses?.[0] || "—"}</div>
      </div>
    ) },
  { k: "stage", label: "Stage", align: "left", get: (d) => d.stage,
    render: (d) => (
      <span className={`px-2 py-0.5 rounded-md text-[11px] whitespace-nowrap ${STAGE_TONE[d.stage] || "bg-[#2c2c2c] text-[#b4b4b4]"}`}>
        {d.stage || "—"}
      </span>
    ) },
  { k: "market", label: "Market", align: "left", get: (d) => d.market || "",
    render: (d) => <span className="text-[#8a8a8a] text-[12px]">{d.market || "—"}</span> },
  { k: "sf", label: "SF", align: "right", get: (d) => d.squareFeet || 0,
    render: (d) => sf0(d.squareFeet) },
  { k: "occ", label: "Occ", align: "right", get: (d) => d._m?.occupancy ?? -1,
    render: (d) => {
      const o = d._m?.occupancy;
      if (!Number.isFinite(o)) return "—";
      return <span className={o < 0.85 ? "text-[#c8922f]" : "text-[#b4b4b4]"}>{pct(o)}</span>;
    } },
  { k: "inplace", label: "In place", align: "right", get: (d) => d._m?.inPlace ?? -1,
    render: (d) => psf(d._m?.inPlace) },
  { k: "market_rent", label: "Market", align: "right", get: (d) => d._m?.market ?? -1,
    render: (d) => psf(d._m?.market) },
  { k: "gap", label: "Gap", align: "right", get: (d) => d._m?.gap ?? -9999,
    render: (d) => {
      const g = d._m?.gap;
      if (!Number.isFinite(g)) return "—";
      return (
        <span className={g > 0 ? "text-[#4f9d76]" : g < 0 ? "text-[#e93529]" : "text-[#8a8a8a]"}>
          {g > 0 ? "+" : ""}{g.toFixed(2)}
        </span>
      );
    } },
  { k: "upside", label: "M2M / yr", align: "right", get: (d) => d._m?.upside ?? -1e12,
    render: (d) => {
      const u = d._m?.upside;
      if (!Number.isFinite(u)) return "—";
      return <span className={u > 0 ? "text-[#4f9d76]" : "text-[#8a8a8a]"}>{money(u)}</span>;
    } },
  { k: "opex", label: "OpEx/SF", align: "right",
    get: (d) => (d._m?.opexAnnual && d.squareFeet ? d._m.opexAnnual / d.squareFeet : -1),
    render: (d) => {
      const v = d._m?.opexAnnual && d.squareFeet ? d._m.opexAnnual / d.squareFeet : null;
      return v ? <span title={d._m.opexBasis}>{psf(v)}</span> : "—";
    } },
  { k: "rolling", label: "Rolls ≤2y", align: "right",
    get: (d) => (d._m?.rollingSf && d.squareFeet ? d._m.rollingSf / d.squareFeet : -1),
    render: (d) => {
      const v = d._m?.rollingSf && d.squareFeet ? d._m.rollingSf / d.squareFeet : null;
      if (!Number.isFinite(v)) return "—";
      return <span className={v > 0.25 ? "text-[#c8922f]" : "text-[#8a8a8a]"}>{pct(v)}</span>;
    } },
  { k: "evidence", label: "Comps", align: "right", get: (d) => d._comps ?? -1,
    render: (d) => {
      const n = d._comps;
      if (!n) return <span className="text-[#7a7a7a]">—</span>;
      return <span className={n < 3 ? "text-[#e93529]" : n < 8 ? "text-[#c8922f]" : "text-[#b4b4b4]"}>{n}</span>;
    } },
  { k: "price", label: "Asking", align: "right", get: (d) => d.askingPrice || 0,
    render: (d) => money(d.askingPrice) },
];

export default function PipelineTable({ deals, onSelectDeal }) {
  const [metrics, setMetrics] = useState(null);
  const [sort, setSort] = useState({ k: "upside", dir: "desc" });
  const [compare, setCompare] = useState(new Set());

  useEffect(() => {
    let live = true;
    loadPipelineMetrics().then((m) => { if (live) setMetrics(m); });
    return () => { live = false; };
  }, []);

  const rows = useMemo(() => {
    const byId = metrics?.byDealId || {};
    const byKey = metrics?.compsByKey || {};
    const named = (d) => {
      const m = d.sourceNotes?.match(/^Name:\s*(.+)$/m);
      return (m ? m[1] : null) || d.addresses?.[0] || "Untitled";
    };
    const out = deals.map((d) => ({
      ...d,
      _name: named(d),
      _m: byId[d.id] || null,
      _comps: byKey[d.compDbDealId || d.comp_db_deal_id] ?? null,
    }));
    const col = COLUMNS.find((c) => c.k === sort.k) || COLUMNS[0];
    return out.sort((a, b) => {
      const va = col.get(a), vb = col.get(b);
      const cmp = typeof va === "string" ? String(va).localeCompare(String(vb)) : (va - vb);
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [deals, metrics, sort]);

  const picked = rows.filter((r) => compare.has(r.id));

  function toggleSort(k) {
    setSort((s) => (s.k === k ? { k, dir: s.dir === "asc" ? "desc" : "asc" } : { k, dir: "desc" }));
  }
  function toggleCompare(id) {
    setCompare((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else if (n.size < 4) n.add(id);
      return n;
    });
  }

  return (
    <div className="space-y-3">
      {/* side-by-side comparison, the first complaint */}
      {picked.length > 1 && (
        <div className="bg-[#242424] border border-[#3a3a3a] rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 border-b border-[#3a3a3a] flex items-center justify-between">
            <span className="text-[13px] font-semibold text-[#f4f4f4]">
              Comparing {picked.length} deals
            </span>
            <button onClick={() => setCompare(new Set())}
              className="text-[11px] text-[#8a8a8a] hover:text-[#f4f4f4]">clear</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <tbody className="divide-y divide-[#303030]">
                {[
                  ["Deal", (d) => d._name],
                  ["Stage", (d) => d.stage],
                  ["SF", (d) => sf0(d.squareFeet)],
                  ["Occupancy", (d) => pct(d._m?.occupancy)],
                  ["In place", (d) => psf(d._m?.inPlace)],
                  ["Market", (d) => psf(d._m?.market)],
                  ["Gap", (d) => (Number.isFinite(d._m?.gap) ? `${d._m.gap > 0 ? "+" : ""}${d._m.gap.toFixed(2)}` : "—")],
                  ["Mark to market", (d) => money(d._m?.upside)],
                  ["OpEx/SF", (d) => (d._m?.opexAnnual && d.squareFeet ? psf(d._m.opexAnnual / d.squareFeet) : "—")],
                  ["Rolls ≤2y", (d) => (d._m?.rollingSf && d.squareFeet ? pct(d._m.rollingSf / d.squareFeet) : "—")],
                  ["Comps", (d) => d._comps ?? "—"],
                  ["Asking", (d) => money(d.askingPrice)],
                ].map(([label, fn]) => (
                  <tr key={label}>
                    <td className="px-4 py-1.5 text-[#8a8a8a] w-40">{label}</td>
                    {picked.map((d) => (
                      <td key={d.id} className="px-4 py-1.5 text-[#f4f4f4] tabular-nums">{fn(d)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-[#242424] border border-[#3a3a3a] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#3a3a3a]">
                <th className="w-8" />
                {COLUMNS.map((c) => (
                  <th key={c.k}
                      onClick={() => toggleSort(c.k)}
                      className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-wider cursor-pointer select-none
                                  ${c.align === "right" ? "text-right" : "text-left"}
                                  ${sort.k === c.k ? "text-[#f04f44]" : "text-[#8a8a8a] hover:text-[#b4b4b4]"} ${c.w || ""}`}>
                    {c.label}
                    {sort.k === c.k && <span className="ml-1">{sort.dir === "asc" ? "↑" : "↓"}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#303030]">
              {!metrics && (
                <tr><td colSpan={COLUMNS.length + 1} className="px-4 py-8 text-center text-[12px] text-[#8a8a8a]">
                  Loading the comp-database figures…
                </td></tr>
              )}
              {rows.map((d) => (
                <tr key={d.id}
                    className={`hover:bg-[#2c2c2c] cursor-pointer ${compare.has(d.id) ? "bg-[rgba(233,53,41,0.06)]" : ""}`}
                    onClick={() => onSelectDeal?.(d)}>
                  <td className="px-2 py-2" onClick={(e) => { e.stopPropagation(); toggleCompare(d.id); }}>
                    <input type="checkbox" readOnly checked={compare.has(d.id)}
                           className="accent-[#e93529] cursor-pointer" />
                  </td>
                  {COLUMNS.map((c) => (
                    <td key={c.k}
                        className={`px-3 py-2 text-[12.5px] tabular-nums ${c.align === "right" ? "text-right" : "text-left"} text-[#b4b4b4]`}>
                      {c.render(d)}
                    </td>
                  ))}
                </tr>
              ))}
              {metrics && rows.length === 0 && (
                <tr><td colSpan={COLUMNS.length + 1} className="px-4 py-8 text-center text-[12px] text-[#8a8a8a]">
                  No deals match the filters.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-[#7a7a7a] px-1">
        Tick up to four deals to compare them side by side. An em dash means the
        figure is not held — not that it is zero.
      </p>
    </div>
  );
}
